import {
  analiseCadastralSchema,
  configurarMiraScoreSchema,
  integracaoMiraScoreSchema,
  type AnaliseCadastral,
  type ConfigurarMiraScore,
  type IntegracaoMiraScore,
} from '@locaveri/shared'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { z } from 'zod'

import { apiDelete, apiGet, apiPost, apiPut } from '@/lib/api'
import { ROTAS_DA_ANALISE_CADASTRAL } from '@/lib/endpoints'
import { usePermissao } from '@/lib/permissoes'

/**
 * ANÁLISE CADASTRAL (Mira Score) — a camada de dados do painel.
 *
 * Os schemas são os de `@locaveri/shared`: a API e o painel validam com o MESMO
 * contrato. Ver `docs/integracao-mira-score.md`.
 */

export const chavesDaAnaliseCadastral = {
  integracao: () => ['analise-cadastral', 'integracao'] as const,
  daReserva: (reservaId: string) => ['analise-cadastral', 'reserva', reservaId] as const,
}

const analiseDaReservaSchema = z.object({ analise: analiseCadastralSchema.nullable() })

/**
 * A situação da integração. Só busca para quem pode ler análise — para os
 * outros papéis a fila mostra o score interno como sempre mostrou.
 */
export function useIntegracaoMiraScore(): UseQueryResult<IntegracaoMiraScore> {
  const podeLer = usePermissao('credit_analysis.read')
  return useQuery({
    queryKey: chavesDaAnaliseCadastral.integracao(),
    queryFn: () => apiGet(ROTAS_DA_ANALISE_CADASTRAL.integracao, integracaoMiraScoreSchema),
    enabled: podeLer,
    // Muda quando o gestor cola a chave — não a cada minuto.
    staleTime: 5 * 60_000,
  })
}

export function useAnaliseDaReserva(reservaId: string): UseQueryResult<AnaliseCadastral | null> {
  const podeLer = usePermissao('credit_analysis.read')
  return useQuery({
    queryKey: chavesDaAnaliseCadastral.daReserva(reservaId),
    queryFn: async () =>
      (await apiGet(ROTAS_DA_ANALISE_CADASTRAL.daReserva(reservaId), analiseDaReservaSchema))
        .analise,
    enabled: podeLer,
    // Enquanto o cliente não autoriza, a análise muda sozinha (o Mira avisa).
    refetchInterval: 60_000,
  })
}

/** Pede a análise à mão — tem custo para a locadora. */
export function useSolicitarAnalise(
  reservaId: string,
): UseMutationResult<AnaliseCadastral, Error, void> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiPost(ROTAS_DA_ANALISE_CADASTRAL.daReserva(reservaId), analiseCadastralSchema, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['analise-cadastral'] })
      void queryClient.invalidateQueries({ queryKey: ['reservas', 'fila'] })
    },
  })
}

export function useConfigurarMiraScore(): UseMutationResult<
  IntegracaoMiraScore,
  Error,
  ConfigurarMiraScore
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (corpo) =>
      apiPut(
        ROTAS_DA_ANALISE_CADASTRAL.integracao,
        integracaoMiraScoreSchema,
        configurarMiraScoreSchema.parse(corpo),
      ),
    onSuccess: (integracao) => {
      queryClient.setQueryData(chavesDaAnaliseCadastral.integracao(), integracao)
    },
  })
}

export function useTestarMiraScore(): UseMutationResult<IntegracaoMiraScore, Error, void> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiPost(ROTAS_DA_ANALISE_CADASTRAL.testarIntegracao, integracaoMiraScoreSchema, {}),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: chavesDaAnaliseCadastral.integracao() })
    },
  })
}

export function useRemoverMiraScore(): UseMutationResult<unknown, Error, void> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiDelete(ROTAS_DA_ANALISE_CADASTRAL.integracao, z.unknown()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chavesDaAnaliseCadastral.integracao() })
    },
  })
}
